import { createServerFn } from '@tanstack/react-start';
import { setCookie } from '@tanstack/react-start/server';

import { externalScreenerControllerGetScreener } from '@/api/core-api';
import { getClient } from '@/utils/get-api-client';
import {
  readExternalSession,
  renewExternalSession,
} from '@/utils/session/external-session';

/**
 * What this browser has already done with the link's study, from its
 * external session for the project:
 *
 * - `NONE`: no session (or it has expired): ask for the email.
 * - `SCREENER` / `ADMITTED`: an application in progress: resume it (the
 *   interview, or the unmoderated study's start page).
 * - `RECEIVED`: filed but not admitted: the same neutral screen as at filing.
 * - `COMPLETED`: the interview was completed here: turn the link away.
 */
export type ExternalInviteResumeState =
  | 'NONE'
  | 'SCREENER'
  | 'ADMITTED'
  | 'RECEIVED'
  | 'COMPLETED';

const ADMITTED = new Set(['INVITED', 'SCHEDULED']);
const DONE = new Set(['ATTENDED', 'PAID']);

export const getExternalInviteResumeState = createServerFn()
  .validator((data: { projectId: string }) => data)
  .handler(async ({ data }): Promise<ExternalInviteResumeState> => {
    const found = await readExternalSession(data.projectId);

    if (!found) {
      return 'NONE';
    }

    if (found.session.completedAt) {
      return 'COMPLETED';
    }

    // The application itself, in case the session outlived it or the
    // interview was completed without the completion reaching the proxy.
    const response = await externalScreenerControllerGetScreener({
      client: getClient(found.session.userId),
      path: { projectId: data.projectId },
      throwOnError: false,
    });

    const status = response.data?.existingResponse?.status;

    if (!status) {
      return 'NONE';
    }

    renewExternalSession(found.sessionId, found.session, setCookie);

    if (status === 'DRAFT') return 'SCREENER';
    if (ADMITTED.has(status)) return 'ADMITTED';
    if (DONE.has(status)) return 'COMPLETED';
    return 'RECEIVED';
  });
